import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { browserTimeZone, withBrowserTimeZone } from 'src/app/shared/utils/browser-time-zone';

export interface widgetFilterPayload{
  module:string,
  filter_value_name:widgetFilterArr[]
}
export interface widgetFilterArr{
  widgetName: string,
  widgetValue:string,
  dealTypeId?:string,
  endDate?:string,
  startDate?:string
}

@Injectable({
  providedIn: 'root'
})
export class WidgetService {
  BASE_URL: any;
  QUOTES_URL:any
  public dealdata$: Subject<any> = new Subject();
  public Lost_Reasons$: Subject<any> = new Subject();
  public Abandoned_Reasons$: Subject<any> = new Subject();
  public Activity_Report$: Subject<any> = new Subject();
  public Deals_Won_By_User_Graph$: Subject<any> = new Subject();
  public Deals_Per_User$: Subject<any> = new Subject();
  public Create_VS_Won_Deals_Graph$: Subject<any> = new Subject();
  public Lost_Deal$: Subject<any> = new Subject();
  public Won_Deal$: Subject<any> = new Subject();
  public Time_Per_Stage$: Subject<any> = new Subject();
  public Source_of_Contact$: Subject<any> = new Subject();
  public Sales_Forecast$: Subject<any> = new Subject();
  public deal_type_id$: Subject<any> = new Subject();
  public dealType_List$: Subject<any> = new Subject();
  public activeWidgets$: BehaviorSubject<any> = new BehaviorSubject([]);
  public Top_Deals$: Subject<any> = new Subject();
  public Deal_By_Type_And_Stages$: Subject<any> = new Subject();
  public Deal_Stages_Data$: Subject<any> = new Subject();
  public dbSaveFilterVal$: Subject<any> = new Subject();
  public showFunnel$: Subject<boolean> = new Subject();
  public showSaveFilterBtn$: BehaviorSubject<boolean> = new BehaviorSubject(false);

constructor(private http: HttpClient) {
    this.BASE_URL = environment.LEAD_MANAGEMENT_URL;
    this.QUOTES_URL=environment.QUOTE_MANAGEMENT_URL
  }

  getWidgetData(data: any = ''): Observable<any> {
    // "Current month" and custom-range days are the user's, not the server's (UTC).
    return this.http.post(`${this.BASE_URL}/widget/lead-widget`, withBrowserTimeZone(data)).pipe(
      _.tap(
        data => data,
        error => error
      ));
  }

  Loadmorenewdeals(data: any = ''): Observable<any> {
    return this.http.post(`${this.BASE_URL}/load-more-deals`, data).pipe(
      _.tap(
        data => data,
        error => error
      ));
  }

  storeWidgetFilter(widgetFilterArr:widgetFilterArr[]): Observable<widgetFilterPayload>{
    let payload:widgetFilterPayload = {
      module:"leadWidget",
      filter_value_name:widgetFilterArr
    }
    return this.http.post<widgetFilterPayload>(`${this.BASE_URL}/save-widget-filters`,payload).pipe(
      _.tap(
        data => data,
        error => error
      ));
  }

  getHowAreMySales(data:any=''): Observable<any> {
    // "This month" on the chart is the user's month, not the server's (UTC).
    return this.http.post(`${this.QUOTES_URL}/quotesListing/how-are-my-sales`, { timezone: browserTimeZone() }).pipe(
      _.tap(
        data => data,
        error => error
      ));
  }

  getQuotesSalesSummary(): Observable<any> {
    return this.http.get(`${this.BASE_URL}/common-data/quotes-sales-summary`).pipe(
      _.tap(
        data => data,
        error => error
      ));
  }

  getSalesBenchmarkAPI(payload: any): Observable<any> {
    // "Quotes this month" is counted in the user's month, not the server's (UTC).
    return this.http.post(`${environment.QUOTE_MANAGEMENT_URL}/quotesListing/sales-benchmark`, { ...payload, timezone: browserTimeZone() }).pipe(
      _.tap(
        data => data,
        error => error
      ));
  }

  getActivities(payload: any = {}): Observable<any> {
    return this.http.post(`${environment.QUOTE_MANAGEMENT_URL}/dashboard-activities/getActivities`, payload).pipe(
      _.tap(
        data => data,
        error => error
      ));
  }

  dismissActivity(payload: any): Observable<any> {
    return this.http.post(`${environment.QUOTE_MANAGEMENT_URL}/dashboard-activities/dismiss`, payload).pipe(
      _.tap(
        data => data,
        error => error
      ));
  }

  getCustomDateTitles(): Observable<any> {
    // Card #356: these titles only fill the Sales Cycle settings' date-field picker, which shows
    // its own loading state. The request took 4.5-6 s on dev (a full scan server-side), and with
    // the global spinner it held the full-screen "Loading" overlay over the whole dashboard.
    // POST (the server's same handler) so it can carry body.hideLoader, the SpinnerInterceptor's
    // existing opt-out; the server reads nothing from the body.
    return this.http.post(`${this.QUOTES_URL}/quotesListing/custom-date-titles`, { hideLoader: true }).pipe(
      _.tap(
        data => data,
        error => error
      ));
  }

  /**
   * Reads the sales cycle list. Card #170 issue 7: this no longer writes.
   *
   * The parameter is typed to paging only, ON PURPOSE. The endpoint still
   * accepts settings for backward compatibility, so a call site that passed
   * them would quietly write on what is meant to be a read — and the symptom,
   * "my settings reverted", is near-impossible to trace back here.
   */
  getSalesCycleQuotes(payload: { page?: number; limit?: number; offset?: number } = {}): Observable<any> {
    return this.http.post(`${this.QUOTES_URL}/quotesListing/sales-cycle-quotes`, payload);
  }

  /**
   * Writes the sales cycle settings. Card #170 issue 7 -- saving used to go
   * through getSalesCycleQuotes, so the same request both read the list and
   * changed the configuration.
   */
  saveSalesCycleSettings(payload: {
    followUpDateField?: string | string[];
    followUpDateFields?: string | string[];
    daysBefore: number | null;
    pastDueDateField?: string | string[];
    pastDueDateFields?: string | string[];
    [key: string]: any;
  }): Observable<any> {
    return this.http.post(`${this.QUOTES_URL}/quotesListing/sales-cycle-settings`, payload);
  }

  dismissSalesCycleQuote(payload: any): Observable<any> {
    return this.http.post(`${this.QUOTES_URL}/quotesListing/dismiss-sales-cycle-quote`, payload).pipe(
      _.tap(
        data => data,
        error => error
      ));
  }

  getQuoteStagesList(): Observable<any> {
    return this.http.get(`${this.QUOTES_URL}/quoteStagesList`).pipe(
      _.tap(
        data => data,
        error => error
      ));
  }

  updateQuoteStage(payload: any): Observable<any> {
    return this.http.post(`${this.QUOTES_URL}/quotesListing/quoteStagesUpdate`, payload).pipe(
      _.tap(
        data => data,
        error => error
      ));
  }

  updateQuoteStatus(payload: any): Observable<any> {
    return this.http.post(`${this.QUOTES_URL}/quotesListing/quoteStatus`, payload).pipe(
      _.tap(
        data => data,
        error => error
      ));
  }
}

